import { ContentLanguage } from 'models/settings';

// Supported locales (same as i18n-server.tsx)
export const SUPPORTED_LOCALES = ['en-US', 'ja-JP', 'ko-KR', 'zh-CN', 'zh-TW'] as const;
export type SupportedLocale = typeof SUPPORTED_LOCALES[number];

// Comprehensive language code mappings for intelligent browser language detection
export const LANGUAGE_MAPPINGS: Record<string, SupportedLocale> = {
  // English variants
  en: 'en-US',
  'en-GB': 'en-US',
  'en-AU': 'en-US',
  'en-CA': 'en-US',
  'en-IE': 'en-US',
  'en-NZ': 'en-US',
  'en-ZA': 'en-US',
  'en-IN': 'en-US',

  // Japanese variants
  ja: 'ja-JP',
  'ja-JP': 'ja-JP',

  // Korean variants
  ko: 'ko-KR',
  'ko-KR': 'ko-KR',

  // Chinese variants
  zh: 'zh-CN', // Default Chinese to Simplified
  'zh-CN': 'zh-CN', // Simplified Chinese (Mainland China)
  'zh-Hans': 'zh-CN', // Simplified Chinese
  'zh-TW': 'zh-TW', // Traditional Chinese (Taiwan)
  'zh-Hant': 'zh-TW', // Traditional Chinese
  'zh-HK': 'zh-TW', // Traditional Chinese (Hong Kong)
  'zh-MO': 'zh-TW', // Traditional Chinese (Macau)
  'zh-SG': 'zh-CN', // Simplified Chinese (Singapore)
};

/**
 * Maps browser language to a supported locale with intelligent fallbacks
 * @param browserLanguage - Browser language from navigator.language
 * @param fallback - Fallback language if no mapping found
 * @returns Mapped supported language or fallback
 */
export function mapBrowserLanguageToSupported(
  browserLanguage: string,
  fallback: SupportedLocale = 'en-US'
): SupportedLocale {
  if (!browserLanguage) {
    return fallback;
  }

  // Normalize the input (case-insensitive matching)
  const normalizedInput = browserLanguage.toLowerCase();

  // Check if exact match exists (case-insensitive)
  const exactMatch = SUPPORTED_LOCALES.find((locale) => locale.toLowerCase() === normalizedInput);
  if (exactMatch) {
    return exactMatch;
  }

  // Check language mappings for intelligent matching
  const mappingMatch = Object.entries(LANGUAGE_MAPPINGS).find(
    ([key]) => key.toLowerCase() === normalizedInput
  );
  if (mappingMatch) {
    return mappingMatch[1];
  }

  // Try matching just the language part (before the dash)
  const languageCode = normalizedInput.split('-')[0];
  const languageCodeMatch = Object.entries(LANGUAGE_MAPPINGS).find(
    ([key]) => key.toLowerCase() === languageCode
  );
  if (languageCodeMatch) {
    return languageCodeMatch[1];
  }

  // No match found, return fallback
  return fallback;
}

/**
 * Converts language from i18n format (en-US) to ContentLanguage format (EN_US)
 * @param language - Language in i18n format
 * @returns Language in ContentLanguage format
 */
export function convertToContentLanguage(language: string): ContentLanguage {
  return language.toUpperCase().replace('-', '_') as ContentLanguage;
}

/**
 * Converts language from ContentLanguage format (EN_US) to i18n format (en-US)
 * @param language - Language in ContentLanguage format
 * @returns Language in i18n format
 */
export function convertToI18nLanguage(language: ContentLanguage | string): SupportedLocale {
  // Handle both formats as input
  if (language.includes('_')) {
    // Convert from ContentLanguage format: 'EN_US' -> 'en-US'
    const parts = language.split('_');
    if (parts.length === 2) {
      const converted = `${parts[0].toLowerCase()}-${parts[1].toUpperCase()}`;
      // Ensure it's a supported locale
      return SUPPORTED_LOCALES.find((locale) => locale === converted) || 'en-US';
    }
  }

  // Input is already in i18n format, validate it's supported
  const supportedMatch = SUPPORTED_LOCALES.find(
    (locale) => locale.toLowerCase() === language.toLowerCase()
  );
  return supportedMatch || 'en-US';
}

/**
 * Validates and normalizes any language input to a supported i18n format
 * Handles browser languages, ContentLanguage format, and validates against supported locales
 * @param language - Language in any format
 * @param fallback - Fallback language if input is not supported
 * @returns Normalized language in i18n format
 */
export function validateAndNormalizeLanguage(
  language: string,
  fallback: SupportedLocale = 'en-US'
): SupportedLocale {
  if (!language) {
    return fallback;
  }

  // If it's in ContentLanguage format (contains underscore), convert first
  if (language.includes('_')) {
    return convertToI18nLanguage(language);
  }

  // Otherwise, treat as browser language and map intelligently
  return mapBrowserLanguageToSupported(language, fallback);
}

/**
 * Gets the list of supported languages in i18n format
 * @returns Array of supported locale strings
 */
export function getSupportedLanguages(): readonly SupportedLocale[] {
  return SUPPORTED_LOCALES;
}

/**
 * Checks if a language is supported
 * @param language - Language to check
 * @returns True if language is supported
 */
export function isSupportedLanguage(language: string): language is SupportedLocale {
  return SUPPORTED_LOCALES.includes(language as SupportedLocale);
}

/**
 * Gets the list of supported languages in ContentLanguage format
 * @returns Array of supported languages in EN_US format
 */
export function getSupportedContentLanguages(): ContentLanguage[] {
  return SUPPORTED_LOCALES.map(convertToContentLanguage);
}
